import { useQuery } from '@tanstack/react-query'

import type { BusinessCreate } from 'src/features/business/shared/schemas/business'
import type { LynkWorkflow } from 'src/features/lynk/schemas/workflows'
import { postRequest } from 'src/lib/api-client'

import type { BusinessInfo } from '../schemas/business-create'

export enum WorkflowPreviewStatus {
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  MISSING_DATA = 'missing-data',
}

export type WorkflowPreview = {
  name: LynkWorkflow
  status: WorkflowPreviewStatus
  missingFields: (keyof BusinessInfo)[]
  workflowEnabled: boolean
}

export type FirstWorkflowPreviewResponse = {
  workflows: WorkflowPreview[]
}

type BackendWorkflowPreview = {
  name: string
  status: 'completed' | 'incomplete'
  missingFields: string[]
  workflowEnabled: boolean
}

type BackendWorkflowsPreviewResponse = {
  workflows: BackendWorkflowPreview[]
}

const toCamelCase = (value: string) => value.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())

export const getFirstWorkflowPreview = async (payload: BusinessCreate): Promise<FirstWorkflowPreviewResponse> => {
  const response = await postRequest<BackendWorkflowsPreviewResponse>('/lynk/workflows-preview', payload)

  if (!response?.workflows) {
    return { workflows: [] }
  }

  const workflows: WorkflowPreview[] = response.workflows
    .map((workflow) => {
      let status = WorkflowPreviewStatus.INCOMPLETE
      if (workflow.status === 'completed') {
        status = WorkflowPreviewStatus.COMPLETE
      } else if (workflow.missingFields?.length > 0) {
        status = WorkflowPreviewStatus.MISSING_DATA
      }

      return {
        name: workflow.name,
        status,
        missingFields: (workflow.missingFields || []).map(toCamelCase),
        workflowEnabled: workflow.workflowEnabled,
      }
    })
    .filter((workflow): workflow is WorkflowPreview => workflow !== null)

  return { workflows }
}

export const useFirstWorkflowPreview = (payload: BusinessCreate) => {
  return useQuery({
    queryKey: ['first-workflow-preview', payload],
    queryFn: () => getFirstWorkflowPreview(payload),
    staleTime: 1000 * 60,
  })
}
